import * as STD  from '@emersya/mpa-std-lib';

abstract class DOMComponent<
	TNode extends HTMLElement | null,
	TComponent extends DOMComponent<TNode, TComponent, TConfiguration>,
	TConfiguration extends DOMComponent.IConfiguration
> extends Component<TComponent, TConfiguration> {
	static isInsertionEnabled = true;

	static getFromDOM(inDOMElement : Node) {
		let DOMElement : HTMLElement | null = <HTMLElement> ((inDOMElement instanceof Element) ?
			inDOMElement :
			inDOMElement.parentElement);
		while (
			DOMElement &&
			(
				(DOMElement.dataset.componentType === undefined) ||
				DOMElement.classList.contains('fakeComponent')
			)
			)
		{
			DOMElement = DOMElement.parentElement;
		}
		if (DOMElement === null) {
			return null;
		}
		return <DOMComponent<any, any, any>> (<any> DOMElement).details.component;
	};

	static override initialize() {
		Component.initialize.call(this);
	};

	abstract readonly component_DOM : TNode;
	override component_isVirtual = false;

	override async component_remove() {
		if (this.component_DOM) {
			this.component_DOM.remove();
		}
		await super.component_remove();
	};

	override async component_appendInnerChild(
		component : Component<any, any>,
		parent : HTMLElement | null = null
	)
	{
		await super.component_appendInnerChild(component, parent);
		if (component instanceof DOMComponent) {
			if (!this.component_DOM) {
				this.component_throwUserError('Cannot call appendInnerChild on a component that have null DOM!');
			}
			if ((<any> component.static).isInsertionEnabled) {
				if (parent) {
					if (!this.component_DOM.contains(parent)) {
						this.component_throwUserError(
							'Cannot append a component to a DOMNode that is not contained in the root component DOM!',
							{
								componentToAppend : component,
								DOMNode :           parent
							}
						);
					}
					parent.appendChild(component.component_DOM);
				} else {
					this.component_DOM.appendChild(component.component_DOM);
				}
			}
		}
	};

	override async component_appendOuterChild(
		component : Component<any, any>,
		_slot : string | HTMLElement
	)
	{
		await super.component_appendOuterChild(component, _slot);
		if (!(component instanceof DOMComponent)) {
			return;
		}
		if (_slot instanceof HTMLElement) {
			_slot.appendChild(component.component_DOM);
		} else if (component.component_parentSlot) {
			if (!(component.component_parentSlot instanceof DOMComponent)) {
				throw new Error('Not implemented');
			}
			component.component_parentSlot.component_DOM.appendChild(component.component_DOM);
		} else {
			if (!this.component_DOM) {
				this.component_throwUserError('Cannot call appendOuterChild with a component that have null DOM', {
					withComponent : component
				});
			}
			this.component_DOM.appendChild(component.component_DOM);
		}
	};

	override async component_onActivationChange(event : STD.events.Event<any, Component<any, any>>) {
		await super.component_onActivationChange(event);
		if (event.subType === 'enabled') {
			if (this.component_DOM) {
				this.component_DOM.classList.remove('disabled');
			}
			if (this.component_parent instanceof DOMComponent) {
				await this.component_dispatchVisibilityUpdate(this.component_parent.component_childIsVisible(this));
			} else {
				await this.component_dispatchVisibilityUpdate(this.component_isVisible);
			}
		} else {
			if (this.component_DOM) {
				this.component_DOM.classList.add('disabled');
			}
			await this.component_dispatchVisibilityUpdate(false);
		}
	};

	override async component_start(ev : STD.events.Event<any, Component<any, any>>) {
		await super.component_start(ev);
		if (this.component_DOM) {
			this.component_DOM.classList.remove('unstarted');
		}
	};

	protected component_finalizeDOM() {
		if (!this.component_DOM) {
			this.component_throwUserError('DOM is not initialized!');
		}
		if (this.component_isStarted) {
			this.component_DOM.classList.remove('unstarted');
		} else if (!this.component_configuration[ '@preventDefaultDisplayNone' ]) {
			this.component_DOM.classList.add('unstarted');
		}
		if (!this.component_DOM.details) {
			this.component_DOM.details = {};
		}
		this.component_DOM.details.component = this;
		this.component_DOM.dataset.componentType = this.static.type;
		this.component_DOM.dataset.componentId = this.id;
		this.component_DOM.dataset.componentUid = this.component_uid.toString();
		if (this.component_configuration[ '@classList' ]) {
			for (const c of this.component_configuration[ '@classList' ]) {
				this.component_DOM.classList.add(c);
			}
		}
		if (this.component_configuration[ '@dataset' ]) {
			for (let [ k, v ] of Object.entries(this.component_configuration[ '@dataset' ])) {
				k = STD.string.changeCase(k, 'camel');
				this.component_DOM.dataset[ k ] = v;
			}
		}
		if (this.component_hasTranslation('title-attr')) {
			this.component_DOM.dataset.translationAttr = 'title:' + this.component_getTranslationId('title-attr');
		}
		const inheritanceChain = <(typeof DOMComponent)[]> <any> STD.object.getInheritanceChainFromInstance(<any> this);
		this.component_DOM.classList.add(...inheritanceChain.slice(0, inheritanceChain.indexOf(DOMComponent)).
		map(x => `component-${ x.type }`));
	};

	protected override async component_finalize() {
		await super.component_finalize();
		if (this.component_DOM) {
			this.component_finalizeDOM();
		} else {
			//you must call manually finalizeDOM when DOM would be ready. When call this method is app or component specific
		}
	};

	#component_isReallyVisible = false;
	#component_isVirtuallyVisible = false;

	get component_isVisible() {
		return (this.#component_isReallyVisible || this.#component_isVirtuallyVisible);
	};

	component_virtualVisibilityEnable() {
		this.#component_isVirtuallyVisible = true;
	};

	component_virtualVisibilityDisable() {
		this.#component_isVirtuallyVisible = false;
	};

	//Should be called on the root component by the dev when displayed / hidden
	async component_dispatchVisibilityUpdate(isVisible : boolean, force = false, byPassIsActive = false) {
		if ((isVisible === this.component_isVisible) && !force) {
			return;
		}
		// Cannot be visible and disabled in same time
		if ((isVisible && !this.component_isActive()) && (byPassIsActive !== true)) {
			return;
		}
		this.#component_isReallyVisible = isVisible;
		await this.component_onVisibilityUpdateBeforeChilds(isVisible);
		await this.component_dispatchVisibilityUpdateOnChilds(force, byPassIsActive);
		await this.component_onVisibilityUpdateAfterChilds(isVisible);
	};

	async component_dispatchVisibilityUpdateOnChilds(force = false, byPassIsActive = false) {
		const childs = <DOMComponent<any, any, any>[]> [
			...this.component_innerChilds.filter(x => x instanceof DOMComponent),
			...this.component_outerChilds.filter(x => x instanceof DOMComponent)
		];
		if (this.component_isVisible) {
			for (const c of childs) {
				const childIsVisible = this.component_childIsVisible(c);
				if (c.component_isActive() || byPassIsActive) {
					await c.component_dispatchVisibilityUpdate(childIsVisible, force, byPassIsActive);
				}
			}
		} else {
			for (const c of childs) {
				if (c.component_isVisible) {
					await c.component_dispatchVisibilityUpdate(false);
				}
			}
		}
	};

	component_querySelector(selectorCSS : string) {
		if (!this.component_DOM) {
			this.component_throwUserError('Cannot call querySelector on a component without DOM!', {
				selectorCSS
			});
		}
		selectorCSS = selectorCSS.trim();
		if (selectorCSS === ':scope') {
			return (this.component_DOM);
		}
		return (this.component_DOM.querySelector(selectorCSS));
	};

	component_querySelectorAll(selectorCSS : string) : Element[] {
		if (!this.component_DOM) {
			this.component_throwUserError('Cannot call querySelectorAll on a component without DOM!', {
				selectorCSS
			});
		}
		selectorCSS = selectorCSS.trim();
		if (selectorCSS === ':scope') {
			return ([ this.component_DOM ]);
		}
		return (Array.from(this.component_DOM.querySelectorAll(selectorCSS)));
	};

	component_computeCSSSelector(fromElement : HTMLElement) {
		if (!this.component_DOM) {
			this.component_throwUserError('Cannot compute CSS selector to an element from a component without DOM!', {
				fromElement
			});
		}
		const componentToElementCSSSelector : string[] = [];
		let element : HTMLElement | null = fromElement;
		let rootComponentFound = false;
		while (element) {
			if (element === this.component_DOM) {
				rootComponentFound = true;
				break;
			} else if (!element.parentElement) {
				break;
			}
			if (element.dataset.componentId) {
				componentToElementCSSSelector.unshift(`[data-component-id='${ element.dataset.componentId }']`);
			} else {
				const index = Array.from(element.parentElement.children).indexOf(element);
				componentToElementCSSSelector.unshift(`:nth-child(${ index + 1 })`);
			}
			element = element.parentElement;
		}
		if (!rootComponentFound) {
			this.component_throwUserError('fromElement is not a descendant of the component!', {
				fromElement
			});
		}
		if (componentToElementCSSSelector.length > 0) {
			return componentToElementCSSSelector.join(' > ');
		}
		return ':scope';
	};

	component_stylize(
		style : Partial<CSSStyleDeclaration>,
		type : 'self' | 'others' = 'self'
	)
	{
		if (type === 'self') {
			if (!this.component_DOM) {
				this.component_throwUserError('Cannot set the style of a component which not have DOM!');
			}
			Object.assign(this.component_DOM.style, style);
		} else {
			for (const sibling of this.component_getSiblings()) {
				if ((sibling instanceof DOMComponent) && sibling.component_DOM) {
					Object.assign(sibling.component_DOM.style, style);
				}
			}
		}
	};

	component_setDOMAttributes(
		attributes : Record<string, string>,
		type : 'self' | 'others' = 'self'
	)
	{
		const __ = Object.entries(attributes);
		if (type === 'self') {
			if (!this.component_DOM) {
				this.component_throwUserError('Cannot set DOM attributes on a component which not have DOM!');
			}
			for (const _ of __) {
				this.component_DOM.setAttribute(_[ 0 ], _[ 1 ]);
			}
		} else {
			for (const sibling of this.component_getSiblings()) {
				if ((sibling instanceof DOMComponent) && sibling.component_DOM) {
					for (const _ of __) {
						sibling.component_DOM.setAttribute(_[ 0 ], _[ 1 ]);
					}
				}
			}
		}
	};

	//Should be overridden by sub component if not all of his childs share the same visibility
	//This method is only called if parent is visible
	protected component_childIsVisible(child : Component<any, any>) {
		return (
			(child instanceof DOMComponent ? child.component_isVisible : child.component_isActive()) || !child.component_isDisabled
		);
	};

	//Should be overridden by component if actions must be done on visibility update
	protected async component_onVisibilityUpdateBeforeChilds(isVisible : boolean) {
		await <Promise<void>> <any> void 1;
		void isVisible;
	};

	//Should be overridden by component if actions must be done on visibility update
	protected async component_onVisibilityUpdateAfterChilds(isVisible : boolean) {
		await <Promise<void>> <any> void 1;
		void isVisible;
	};

	constructor(
		descriptor : DOMComponent.IDescriptor,
		configuration : TConfiguration
	)
	{
		super(descriptor, configuration);
		if (this.component_configuration[ '@forceVirtualVisibility' ]) {
			this.#component_isVirtuallyVisible = true;
		}
	};
};
namespace DOMComponent {
	export interface IDescriptor extends Component.IDescriptor {
	};

	export interface IConfiguration extends Component.IConfiguration {
		'@classList'? : string[];
		'@dataset'? : Record<string, string>;
		'@preventDefaultDisplayNone'? : boolean;
		'@forceVirtualVisibility'? : boolean;
	};
}
export default DOMComponent;
import Component from './Component';
